import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HardHat, Pill, Syringe, Bone, Plus, History, Settings as SettingsIcon, X, Save, Trash2 } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";
import { canOwnerWrite } from "../lib/permissions";

const CARE_META = {
  shoeing:     { label: "Shoeing",     icon: HardHat, defaultDays: 42 },
  deworming:   { label: "Deworming",   icon: Pill,    defaultDays: 90 },
  dental:      { label: "Dental",      icon: Bone,    defaultDays: 365 },
};

const HEADER_BG = "#2D6E6E";
const TEAL = "#4A7C7C";
const AMBER = "#E8A838";
const RED = "#C0392B";
const TRACK = "rgba(74,124,124,0.18)";

const statusColor = (s) => {
  if (s === "overdue") return RED;
  if (s === "due_soon") return AMBER;
  return TEAL;
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

function ProgressRing({ percent = 0, color = TEAL, size = 110, stroke = 10, label }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = Math.max(0, Math.min(100, percent)) / 100 * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Care progress ring">
      <circle cx={size / 2} cy={size / 2} r={radius} stroke={TRACK} strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        stroke={color} strokeWidth={stroke} fill="none"
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeDashoffset={circumference / 4}
        strokeLinecap="round"
        style={{ transition: "stroke-dasharray 400ms ease, stroke 250ms ease" }}
      />
      <text x="50%" y="46%" textAnchor="middle" style={{ fontFamily: "Plus Jakarta Sans, Inter, sans-serif", fontWeight: 700, fontSize: 18, fill: "#3B2F1E" }}>
        {label?.top}
      </text>
      <text x="50%" y="62%" textAnchor="middle" style={{ fontFamily: "Inter, sans-serif", fontSize: 10, fill: "#8C9E8A" }}>
        {label?.bottom}
      </text>
    </svg>
  );
}

function CareCard({ summary, onLog, onHistory, onSettings, hideDates, canWrite, role }) {
  const meta = CARE_META[summary.care_type];
  const Icon = meta.icon;
  const interval = summary.interval_days || meta.defaultDays;

  let percent = 0;
  if (summary.status !== "no_history" && summary.last_event_date) {
    percent = Math.min(100, (summary.days_elapsed ?? 0) / interval * 100);
    if (summary.status === "overdue") percent = 100;
  }

  const color = summary.status === "no_history" ? TRACK : statusColor(summary.status);
  const top = summary.status === "no_history" ? "—" : `${Math.max(0, summary.days_elapsed ?? 0)}`;
  const bottom = summary.status === "no_history" ? "Not logged" : `/ ${interval} days`;

  const badge =
    summary.status === "overdue"
      ? <span className="se-badge se-badge-urgent" data-testid={`care-${summary.care_type}-badge-overdue`}>Overdue</span>
      : summary.status === "due_soon"
      ? <span className="se-badge se-badge-warning" data-testid={`care-${summary.care_type}-badge-due-soon`}>Due soon</span>
      : null;

  return (
    <div
      className="se-card p-0 overflow-hidden flex flex-col"
      style={{ borderColor: color === TRACK ? "rgba(212,169,106,0.2)" : `${color}55` }}
      data-testid={`care-card-${summary.care_type}`}
    >
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ background: HEADER_BG, color: "#F5EFE6" }}
      >
        <div className="flex items-center gap-2">
          <Icon size={16} />
          <span className="font-medium" style={{ letterSpacing: "0.02em" }}>{meta.label}</span>
        </div>
        <div className="flex items-center gap-2">{badge}</div>
      </div>

      <div className="p-4 flex flex-col items-center gap-3 flex-1">
        <ProgressRing percent={percent} color={color === TRACK ? "rgba(74,124,124,0.4)" : color} label={{ top, bottom }} />

        {!hideDates ? (
          <div className="text-center text-sm">
            <div style={{ color: "#8C9E8A" }} data-testid={`care-${summary.care_type}-last`}>
              {summary.last_event_date ? `Last: ${fmtDate(summary.last_event_date)}` : "No events yet"}
            </div>
            {summary.next_due_date && (
              <div style={{ color: TEAL, fontWeight: 700, marginTop: 2 }} data-testid={`care-${summary.care_type}-next`}>
                Next: {fmtDate(summary.next_due_date)}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-sm" data-testid={`care-${summary.care_type}-status-only`}>
            <span className="se-badge" style={{ background: `${color}22`, color }}>
              {summary.status.replace("_", " ")}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          {(canWrite || canOwnerWrite(role)) && (
            <button
              className="se-btn"
              style={{ padding: "6px 12px", fontSize: 13, background: HEADER_BG }}
              onClick={() => onLog(summary)}
              data-testid={`care-${summary.care_type}-log-button`}
            >
              <Plus size={14} /> Log
            </button>
          )}
          <button
            className="se-btn se-btn-ghost"
            style={{ padding: "6px 10px", fontSize: 13 }}
            onClick={() => onHistory(summary)}
            data-testid={`care-${summary.care_type}-history-button`}
            aria-label="View history"
          >
            <History size={14} />
          </button>
          {(canWrite || canOwnerWrite(role)) && (
            <button
              className="se-btn se-btn-ghost"
              style={{ padding: "6px 10px", fontSize: 13 }}
              onClick={() => onSettings(summary)}
              data-testid={`care-${summary.care_type}-settings-button`}
              aria-label="Interval settings"
            >
              <SettingsIcon size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CareTracker({ horseId, horseName }) {
  const { user } = useAuth();
  const role = user?.role;
  const canWrite = ["admin", "manager", "vet", "super_admin"].includes(role);
  // ENH-OwnerWrite: owner is a first-class care editor now; no more date-redaction.
  const hideDates = false;

  const [summaries, setSummaries] = useState(null);
  const [error, setError] = useState("");
  const [logFor, setLogFor] = useState(null);   // {care_type}
  const [logForm, setLogForm] = useState({ event_date: new Date().toISOString().slice(0, 10), event_time: "", comment: "", cost: "" });
  const [busy, setBusy] = useState(false);

  const [histFor, setHistFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [histDel, setHistDel] = useState(null);

  const [settingsFor, setSettingsFor] = useState(null);
  const [intervalVal, setIntervalVal] = useState(42);

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get(`/horses/${horseId}/care`);
      setSummaries(data);
    } catch (e) {
      setError(formatApiError(e));
      setSummaries([]);
    }
  };

  useEffect(() => {
    if (horseId && role !== "driver") load();
  }, [horseId, role]);

  const headerAlert = useMemo(() => {
    if (!summaries) return null;
    if (summaries.some((s) => s.status === "overdue")) return "overdue";
    if (summaries.some((s) => s.status === "due_soon")) return "due_soon";
    return null;
  }, [summaries]);

  if (role === "driver") return null; // no access (after hooks per React rules)

  const openLog = (s) => {
    setLogFor(s);
    setLogForm({ event_date: new Date().toISOString().slice(0, 10), event_time: "", comment: "", cost: "" });
  };

  const showCost = ["admin", "manager", "vet", "super_admin"].includes(role);

  const submitLog = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { event_date: logForm.event_date, comment: logForm.comment };
      if (logForm.event_time) payload.event_time = logForm.event_time;
      if (showCost && logForm.cost !== "") payload.cost = Number(logForm.cost);
      await api.post(`/horses/${horseId}/care/${logFor.care_type}/events`, payload);
      setLogFor(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async (s) => {
    setHistFor(s);
    setHistory(null);
    try {
      const { data } = await api.get(`/horses/${horseId}/care/${s.care_type}/events`);
      setHistory(data);
    } catch (e) {
      setError(formatApiError(e));
      setHistory([]);
    }
  };

  const deleteEvent = async () => {
    try {
      await api.delete(`/care-events/${histDel.id}`);
      setHistDel(null);
      // Refresh both history list and summaries
      if (histFor) await openHistory(histFor);
      await load();
    } catch (e) {
      setError(formatApiError(e));
      setHistDel(null);
    }
  };

  const openSettings = (s) => {
    setSettingsFor(s);
    setIntervalVal(s.interval_days || CARE_META[s.care_type].defaultDays);
  };

  const submitSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.patch(`/horses/${horseId}/care/${settingsFor.care_type}/config`, {
        interval_days: Number(intervalVal),
      });
      setSettingsFor(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6" data-testid="care-tracker">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div>
          <div className="se-label">Recurring care</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 22 }}>Care Tracker</h3>
        </div>
        {headerAlert && (
          <span
            className={`se-badge ${headerAlert === "overdue" ? "se-badge-urgent" : "se-badge-warning"}`}
            data-testid="care-header-alert"
          >
            {headerAlert === "overdue" ? "Action needed" : "Due soon"}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="care-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="care-grid">
        {(summaries || []).map((s) => (
          <CareCard
            key={s.care_type}
            summary={s}
            onLog={openLog}
            onHistory={openHistory}
            onSettings={openSettings}
            hideDates={hideDates}
            canWrite={canWrite}
            role={role}
          />
        ))}
      </div>

      {/* Log event modal */}
      {logFor && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={() => setLogFor(null)}>
          <form className="se-card w-full max-w-md" onClick={(e) => e.stopPropagation()} onSubmit={submitLog} data-testid="care-log-form">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading" style={{ fontSize: 18 }}>Log {CARE_META[logFor.care_type].label}</h3>
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setLogFor(null)} style={{ padding: 6 }}><X size={14} /></button>
            </div>
            <p className="text-xs mb-3" style={{ color: "#8C9E8A" }}>
              Next due date will recompute as event date + {logFor.interval_days || CARE_META[logFor.care_type].defaultDays} days.
            </p>
            <label className="se-label block mb-1">Date</label>
            <input
              type="date"
              className="se-input mb-3"
              value={logForm.event_date}
              onChange={(e) => setLogForm({ ...logForm, event_date: e.target.value })}
              required
              data-testid="care-log-date"
            />
            <label className="se-label block mb-1">Time (optional)</label>
            <input
              type="time"
              className="se-input mb-3"
              value={logForm.event_time}
              onChange={(e) => setLogForm({ ...logForm, event_time: e.target.value })}
              data-testid="care-log-time"
            />
            <label className="se-label block mb-1">Comment</label>
            <textarea
              className="se-input"
              style={{ height: 80, padding: "10px 14px" }}
              value={logForm.comment}
              onChange={(e) => setLogForm({ ...logForm, comment: e.target.value })}
              placeholder="Product used, vet name, observations…"
              data-testid="care-log-comment"
            />
            {showCost && (
              <>
                <label className="se-label block mb-1 mt-3">Cost (€) — optional</label>
                <input
                  type="number" min="0" step="0.01" className="se-input"
                  value={logForm.cost}
                  onChange={(e) => setLogForm({ ...logForm, cost: e.target.value })}
                  placeholder="0.00"
                  data-testid="care-log-cost"
                />
              </>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setLogFor(null)}>Cancel</button>
              <button type="submit" className="se-btn" disabled={busy} style={{ background: HEADER_BG }} data-testid="care-log-submit">
                {busy ? "Saving…" : "Log event"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* History modal */}
      {histFor && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={() => setHistFor(null)}>
          <div className="se-card w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="care-history-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading" style={{ fontSize: 18 }}>{CARE_META[histFor.care_type].label} history</h3>
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setHistFor(null)} style={{ padding: 6 }}><X size={14} /></button>
            </div>
            {history === null && <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>}
            {history !== null && history.length === 0 && (
              <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid="care-history-empty">No events yet.</div>
            )}
            <div className="space-y-2">
              {(history || []).map((e) => (
                <div
                  key={e.id}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg"
                  style={{ background: "rgba(74,124,124,0.08)", border: "0.5px solid rgba(74,124,124,0.2)" }}
                  data-testid={`care-history-row-${e.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div style={{ fontWeight: 600 }}>{fmtDate(e.event_date)}</div>
                    <div className="text-xs mt-0.5" style={{ color: "#8C9E8A" }}>
                      {e.logged_by_name} · Next due {fmtDate(e.next_due_date)}
                    </div>
                    {e.comment && <div className="text-sm mt-2 whitespace-pre-wrap">{e.comment}</div>}
                  </div>
                  {canWrite && (
                    <button
                      className="se-btn se-btn-ghost"
                      style={{ padding: 6 }}
                      onClick={() => setHistDel(e)}
                      data-testid={`care-history-delete-${e.id}`}
                      aria-label="Delete event"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {settingsFor && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={() => setSettingsFor(null)}>
          <form className="se-card w-full max-w-sm" onClick={(e) => e.stopPropagation()} onSubmit={submitSettings} data-testid="care-settings-form">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading" style={{ fontSize: 18 }}>{CARE_META[settingsFor.care_type].label} interval</h3>
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setSettingsFor(null)} style={{ padding: 6 }}><X size={14} /></button>
            </div>
            <p className="text-xs mb-3" style={{ color: "#8C9E8A" }}>
              For <strong>{horseName}</strong>. Default: {CARE_META[settingsFor.care_type].defaultDays} days.
            </p>
            <label className="se-label block mb-1">Interval (days)</label>
            <input
              type="number"
              min={1}
              max={3650}
              className="se-input"
              value={intervalVal}
              onChange={(e) => setIntervalVal(e.target.value)}
              required
              data-testid="care-settings-interval"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setSettingsFor(null)}>Cancel</button>
              <button type="submit" className="se-btn" disabled={busy} style={{ background: HEADER_BG }} data-testid="care-settings-submit">
                <Save size={14} /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!histDel}
        onClose={() => setHistDel(null)}
        onConfirm={deleteEvent}
        title="Delete this care event?"
        description={histDel ? `${CARE_META[histDel.care_type].label} on ${fmtDate(histDel.event_date)} will be removed.` : ""}
        confirmLabel="Yes, delete"
        testid="care-event-delete-confirm"
      />
    </div>
  );
}
